import React, { Fragment, useEffect, useState } from 'react';
import { compose } from 'redux';

import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { isDataLoading } from '@shared/axios/axios.utils';
import smartViewTexts from '@shared/i18n/overview/texts';
import DeleteModal from '@shared/components/modals/FullScreenDeleteModal';
import { VIEW_TYPE } from '@shared/constants/dashboard/campaign-table-constants';
import TEST_ID from '@shared/constants/testId.constants';
import { toCancellable } from '@shared/axios';
import { mapObjectSelectedToProps } from '@domain/_redux/custom-map-state-to-props';
import { withCampaigns } from '@domain/campaigns';
import { withMetricsReport } from '@domain/dashboard/metrics-report/with-metrics-report';
import { withLoggedUser } from '@domain/logged-user';
import { WithDataFetchParams } from '@shared/redux/commons.types';
import { sortSegmentsByType } from '@domain/segments/segments-drawer/segments.utils';
import { withSegmentsModal } from '@domain/segments/segments-modal/segments-modal.hocs';
import { isBudgetManagerTagGroup } from '@domain/budget-manager/budget-manager.utils';
import { SegmentsContainer } from '../segments-modal/segments-modal-container';
import { SegmentsDrawer } from './segments-drawer';
import { withSegments } from '../segments.hocs';
import { getGroupBySlug, isTagGroupOrStandardType } from '../segments.utils';
import { Segments, TaggedCampaigns } from '../segments.types';

interface TagGroupFunctionProps {
    groupSlug?: string;
    groupId?: number | string;
    clientId?: number | string;
}

interface SegmentsProps extends WithDataFetchParams<Segments>{
    currentSegments: {
        loadState: string;
        data: Array<Segments>;
        selectedGroup: {
            slug: string;
        };
    }
    loadState: string;
}

interface ModalSelectedGroupProps {
    group?: {
        id?: string;
        name?: string;
        isCreating?: boolean;
        taggedCampaigns?: TaggedCampaigns;
    }
}

interface TagGroupsSelectorImplProps {
    accountId?: number;
    campaigns?: {
        loadState?: string;
    };
    clientSelected?: {
        selected: boolean;
        id: number;
    };
    deleteTagGroup?: (object?: TagGroupFunctionProps) => void;
    getClientTagGroups?: (object?: TagGroupFunctionProps) => void;
    isTagsGroupModalOpen?: boolean;
    setModalSelectedGroup?: (object?: ModalSelectedGroupProps) => void;
    setSelectedGroupBySlug?: (object?: TagGroupFunctionProps) => void;
    tagGroups?: SegmentsProps;
    toggleTagsGroupModal?: () => void;
    loggedUser?: {};
    secondaryColor?: string;
    size?: number;
    isDesktop?: boolean;
}

const TagGroupsSelectorImpl = ({
    accountId,
    campaigns,
    clientSelected,
    deleteTagGroup,
    getClientTagGroups,
    isTagsGroupModalOpen,
    setModalSelectedGroup,
    setSelectedGroupBySlug,
    tagGroups,
    toggleTagsGroupModal,
    loggedUser,
    secondaryColor,
    size,
    isDesktop,
}: TagGroupsSelectorImplProps) => {
    const { selected: isClientSelected, id: clientId } = clientSelected;
    const isLoading = isDataLoading(campaigns.loadState)
    || isDataLoading(tagGroups.loadState);

    const { selectedGroup } = tagGroups;

    const [isDeleteModalOpen, setToggleDeleteModal] = useState(false);
    const [groupToBeDeleted, setGroupToBeDeleted] = useState(null);

    const callGetTagGroupsAction = () => {
        if (!clientId || !isEmpty(tagGroups.data)) return () => {};
        return toCancellable(getClientTagGroups({ clientId }));
    };

    useEffect(callGetTagGroupsAction, [clientId, accountId]);

    const onOpenDeleteGroupModal = (tagGroupSlug) => {
        const tagGroup = Object.values(tagGroups.data)
            .find(group => group.slug === tagGroupSlug);
        setGroupToBeDeleted(tagGroup);
        setToggleDeleteModal(true);
    };

    const openTagsGroupModal = (groupSlug) => {
        const group = getGroupBySlug(tagGroups.data, groupSlug);
        if (isTagsGroupModalOpen && group.isCreating) {
            setSelectedGroupBySlug({ groupSlug: VIEW_TYPE.standard });
        } else {
            setSelectedGroupBySlug({ groupSlug: group.slug });
        }

        toggleTagsGroupModal();
    };

    const onAddNewSegment = () => {
        setModalSelectedGroup({
            group: {
                name: '', isCreating: true, taggedCampaigns: {},
            },
        });
        toggleTagsGroupModal();
    };

    const onEditTagsGroup = (groupSlug) => {
        const group = getGroupBySlug(tagGroups.data, groupSlug);
        setModalSelectedGroup({ group });
        toggleTagsGroupModal();
    };

    const handleGroupChange = (groupSlug) => {
        if (groupSlug === selectedGroup.slug) return;
        setSelectedGroupBySlug({ groupSlug });
    };

    const handleDeleteTagGroup = () => {
        setToggleDeleteModal(false);
        deleteTagGroup({
            groupSlug: groupToBeDeleted.slug,
            groupId: groupToBeDeleted.id,
            clientId,
        });
        setSelectedGroupBySlug({ groupSlug: VIEW_TYPE.standard });
    };

    const getGroupOptions = () => {
        if (!isDataLoading(tagGroups.loadState)) {
            const formattedGroups = Object.values(tagGroups.data).map(
                group => ({
                    ...group,
                    label: group.name,
                    value: group.slug,
                    checked: group.slug === selectedGroup.slug,
                    isDeletable: !isTagGroupOrStandardType(group.slug),
                    isEditable: !isTagGroupOrStandardType(group.slug),
                }),
            ).sort(sortSegmentsByType);

            if (!isClientSelected) {
                return Object.values(formattedGroups).filter(
                    group => !isBudgetManagerTagGroup(group.slug),
                );
            }
            return formattedGroups;
        }
        return [];
    };

    return (
        <Fragment>
            <SegmentsDrawer
                onClickButton={onAddNewSegment}
                options={getGroupOptions()}
                onChange={openTagsGroupModal}
                loggedUser={loggedUser}
                onEditTagsGroup={onEditTagsGroup}
                isLoading={isLoading}
                onOpenDeleteGroupModal={onOpenDeleteGroupModal}
                handleGroupChange={handleGroupChange}
                buttonLabel={smartViewTexts.tagsGroup.createNewGroup}
                secondaryColor={secondaryColor}
                size={size}
                isDesktop={isDesktop}
            />
            {isDeleteModalOpen
                && (
                    <DeleteModal
                        data-testid={TEST_ID.MULTI_SELECTOR.DELETE_TAG_GROUP_MODAL}
                        title={smartViewTexts.deleterTaggedGroupModal.title}
                        subtitle={smartViewTexts.deleterTaggedGroupModal.subtitle}
                        isOpen={isDeleteModalOpen}
                        handleCloseDeleteModal={() => setToggleDeleteModal(false)}
                        onClose={() => setToggleDeleteModal(false)}
                        onDeleteClick={handleDeleteTagGroup}
                    />
                )}
            {isTagsGroupModalOpen && (
                <SegmentsContainer />
            )}
        </Fragment>
    );
};

const mapStateToProps = (state) => {
    const { objectSelected } = mapObjectSelectedToProps(state);
    const {
        client_selected: clientSelected,
        account_selected: accountSelected,
    } = objectSelected;
    return {
        clientSelected,
        accountId: accountSelected && accountSelected.id,
        objectSelected,
    };
};

export const SegmentsDrawerContainer = compose(
    withCampaigns,
    withMetricsReport,
    withSegments,
    withSegmentsModal,
    withLoggedUser,
    connect(mapStateToProps),
)(TagGroupsSelectorImpl);
